import type { List } from '@gonfalon/immutable-shim/immutableShim';

import type { Rollout } from '#utils/flags/records/Rollout';
import type { Variation } from '#utils/flags/records/Variation';
import { createWeightedVariation, sortFallThroughVariations } from '#utils/flags/weightedVariationUtils';

export const handleRolloutLogic = (r: Rollout, variationIndex: number, weight: number, variations: List<Variation>) => {
  //we're only updating variations that are not untracked
  const index = r.variations.findIndex((v) => v.variation === variationIndex && !v._untracked);
  const existingVariations = r.variations
    .toJS()
    .map((d) => (d?._untracked ? undefined : d.variation))
    .filter((v) => v !== undefined);

  if (index === -1) {
    const sortedVariations = sortFallThroughVariations(r.variations);
    const hasCurrentVariationsThatAreSorted = sortedVariations.equals(r.variations);
    /* Add all flag variations to flagConfig.fallthrough.rollout (even if rollout for that variation is 0%) and then
      sort the multivariate percentage rollouts when:
        (1) User creates a new percentage rollout
        (2) Current rollouts are sorted and there more flag variations than percentage rollouts (this will not happen in the future)
    */
    if (variations.size > r.variations.size && hasCurrentVariationsThatAreSorted) {
      //First add all flag variations to flagConfig.fallthrough.rollout even if rollout for that variation is 0%
      return r.update('variations', (vs) => {
        variations.forEach((_v, i) => {
          if (!existingVariations.includes(i)) {
            // oxlint-disable-next-line no-param-reassign
            vs = vs.push(
              createWeightedVariation({
                variation: i,
                weight: variationIndex === i ? weight : 0,
              }),
            );
          }
        });
        //Then sort the list of fallthrough variations
        return sortFallThroughVariations(vs);
      });
    } else {
      //if current variations are not sorted, return unsorted fallthrough variation list:
      return r.update('variations', (vs) =>
        vs.push(
          createWeightedVariation({
            variation: variationIndex,
            weight,
          }),
        ),
      );
    }
  } else {
    //if we are only changing percentages, return updated fallthrough variation list with updated percentages
    return r.updateIn(['variations', index], (wv) => wv.set('weight', weight));
  }
};
